import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Input, Segmented } from 'antd';
import { InfoCircleFilled } from '@ant-design/icons';
import { OTP_PURPOSE } from '@core/constants/auth';
import { useReferralAvailabilityQuery } from '@core/hooks/useAuth';
import { useStoreContext } from '@core/hooks/useStoreContext';
import { useToast } from '@core/hooks/useToast';
import { buildSignupInput, pickContact, useAuthActions } from '@features/auth/useAuthActions';
import AuthBrandMark from '@components/base/AuthBrandMark';
import BusinessDetailsFields from '@components/base/BusinessDetailsFields';
import MobileNumberField, { validateMobileNumber } from '@components/base/MobileNumberField';
import AppModal from '@components/base/AppModal';
import { useCopy } from '@/app/providers/useCopy';

export default function StorefrontAuthModal({
  open,
  mode = 'choice',
  onCancel,
  onAuthenticated,
  title = 'Almost there!',
  subtitle = 'To place your order, log in or sign up.',
}) {
  const [activeMode, setActiveMode] = useState(mode);
  const [contactMethod, setContactMethod] = useState('email');
  const [otpPurpose, setOtpPurpose] = useState(OTP_PURPOSE.SIGNUP);
  const [contact, setContact] = useState(null);
  const [otpToken, setOtpToken] = useState('');
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [otpExpiresAt, setOtpExpiresAt] = useState(0);
  const [approvalMessage, setApprovalMessage] = useState('');
  const [now, setNow] = useState(Date.now());
  const otpInputRefs = useRef([]);
  const [loginForm] = Form.useForm();
  const [signupForm] = Form.useForm();
  const [forgotForm] = Form.useForm();
  const [verifyForm] = Form.useForm();
  const [resetForm] = Form.useForm();
  const [resendForm] = Form.useForm();
  const referralAvailability = useReferralAvailabilityQuery({ enabled: open });
  const auth = useAuthActions();
  const { isBusiness, accountCountry } = useStoreContext();
  const toast = useToast();
  const t = useCopy();

  const showChoice = activeMode === 'choice';
  const showReferralCodeInput = Boolean(
    referralAvailability.data?.show_referral_code_input && activeMode === 'signup',
  );

  useEffect(() => {
    if (!open) return;
    setActiveMode(mode);
    setContactMethod('email');
    setOtpPurpose(OTP_PURPOSE.SIGNUP);
    setContact(null);
    setOtpToken('');
    setOtpDigits(['', '', '', '', '', '']);
    setOtpExpiresAt(0);
    setApprovalMessage('');
    // No country here: MobileNumberField fills in the store's country.
    const defaultMobile = { number: '' };
    loginForm.setFieldValue('mobile', defaultMobile);
    signupForm.setFieldValue('mobile', defaultMobile);
    signupForm.setFieldValue(['business_details', 'business_phone'], defaultMobile);
    forgotForm.setFieldValue('mobile', defaultMobile);
    resendForm.setFieldValue('mobile', defaultMobile);
  }, [forgotForm, loginForm, mode, open, resendForm, signupForm]);

  useEffect(() => {
    if (activeMode !== 'verify' || !otpExpiresAt) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [activeMode, otpExpiresAt]);

  const close = () => {
    auth.resetState();
    signupForm.setFieldValue('referral_code', undefined);
    onCancel?.();
  };

  // Moves the modal to the OTP step for a freshly sent code.
  const enterOtpStep = ({ contact: nextContact, purpose, token, expiresAt }) => {
    setContact(nextContact);
    setOtpPurpose(purpose);
    setOtpToken(token);
    setOtpExpiresAt(expiresAt);
    setNow(Date.now());
    setOtpDigits(['', '', '', '', '', '']);
    verifyForm.setFieldValue('otp', '');
    setActiveMode('verify');
  };

  const onLogin = async (values) => {
    const input = { ...pickContact(values, contactMethod), password: values.password };
    if (await auth.login(input, { form: loginForm })) {
      onAuthenticated?.();
      close();
    }
  };

  const onSignup = async (values) => {
    const step = await auth.startSignup(
      buildSignupInput(
        { ...values, confirmPassword: values.password },
        {
          includeReferral: showReferralCodeInput,
          isBusiness,
          country: accountCountry,
        },
      ),
      {
        form: signupForm,
        successMessage: t('checkContactForOtp', 'Check your email/phone for the OTP.'),
      },
    );
    if (!step) return;
    if (step.awaitingApproval) {
      setApprovalMessage(step.message);
      setActiveMode('approval');
      return;
    }
    enterOtpStep(step);
  };

  const onForgot = async (values) => {
    const step = await auth.startReset(pickContact(values, contactMethod), { form: forgotForm });
    if (step) enterOtpStep(step);
  };

  const onVerify = async (values) => {
    if (!contact) return;
    if (isOtpExpired) {
      toast.error(t('otpExpiredResend', 'OTP expired. Please resend OTP.'));
      return;
    }
    const verified = await auth.verifyOtp(
      {
        contact,
        otp: otpDigits.join('') || String(values.otp || '').trim(),
        purpose: otpPurpose,
        token: otpToken,
      },
      { form: verifyForm },
    );
    if (!verified) return;
    toast.success(t('otpVerified', 'OTP verified.'));
    setActiveMode(otpPurpose === OTP_PURPOSE.RESET_PASSWORD ? 'reset' : 'login');
  };

  const onReset = async (values) => {
    if (!contact) return;
    if (values.new_password !== values.confirm_password) {
      resetForm.setFields([
        { name: 'confirm_password', errors: [t('passwordsDoNotMatch', 'Passwords do not match')] },
      ]);
      return;
    }
    if (await auth.resetPassword(contact, values, { form: resetForm })) setActiveMode('login');
  };

  const onResend = async (values) => {
    const payload = contact || pickContact(values, contactMethod);
    const fresh = await auth.resendOtp(payload, otpPurpose, { form: resendForm });
    if (!fresh) return;
    setContact(payload);
    if (fresh.token) setOtpToken(fresh.token);
    setOtpExpiresAt(fresh.expiresAt);
    setNow(Date.now());
    setOtpDigits(['', '', '', '', '', '']);
    verifyForm.setFieldValue('otp', '');
    if (activeMode !== 'verify') setActiveMode('verify');
  };

  const otpValue = otpDigits.join('');
  const timeLeftMs = Math.max(otpExpiresAt - now, 0);
  const isOtpExpired = otpExpiresAt > 0 && timeLeftMs === 0;
  const timeLeftLabel = `${Math.floor(timeLeftMs / 60000)}:${String(
    Math.floor((timeLeftMs % 60000) / 1000),
  ).padStart(2, '0')}`;

  const onOtpDigitChange = (index, raw) => {
    const value = String(raw || '')
      .replace(/\D/g, '')
      .slice(-1);
    const next = [...otpDigits];
    next[index] = value;
    setOtpDigits(next);
    verifyForm.setFieldValue('otp', next.join(''));
    if (value && index < 5) otpInputRefs.current[index + 1]?.focus?.();
  };

  const onOtpPaste = (event) => {
    const pasted = event.clipboardData?.getData('text')?.replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    event.preventDefault();
    const next = Array.from({ length: 6 }, (_, index) => pasted[index] || '');
    setOtpDigits(next);
    verifyForm.setFieldValue('otp', next.join(''));
    otpInputRefs.current[Math.min(pasted.length, 6) - 1]?.focus?.();
  };

  const onOtpKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus?.();
    }
    if (event.key === 'ArrowLeft' && index > 0) otpInputRefs.current[index - 1]?.focus?.();
    if (event.key === 'ArrowRight' && index < 5) otpInputRefs.current[index + 1]?.focus?.();
  };

  const phoneFields = (formNamePrefix = '') => (
    <Form.Item
      label={t('phoneNumber', 'Phone number')}
      name={`${formNamePrefix}mobile`}
      rules={[
        {
          validator: (_, val) => validateMobileNumber(val, t),
        },
      ]}
    >
      <MobileNumberField placeholder={t('enterMobileNumber', 'Enter mobile number')} />
    </Form.Item>
  );

  const signupFields = (
    <>
      {/* Business signup takes name, email and phone from the business details. */}
      {isBusiness ? null : (
        <>
          <Form.Item
            label={t('fullName', 'Full name')}
            name="full_name"
            rules={[{ required: true }]}
          >
            <Input autoComplete="name" placeholder={t('enterFullName', 'Enter full name')} />
          </Form.Item>
          <Form.Item label={t('email', 'Email')} name="email" rules={[{ required: true }]}>
            <Input
              type="email"
              autoComplete="email"
              placeholder={t('enterEmailAddress', 'Enter email address')}
            />
          </Form.Item>
          {phoneFields()}
        </>
      )}
      {/* Business signup: business details first, password last. */}
      {isBusiness ? <BusinessDetailsFields /> : null}
      <Form.Item
        label={t('createPassword', 'Create password')}
        name="password"
        rules={[{ required: true }]}
      >
        <Input.Password
          autoComplete="new-password"
          placeholder={t('enterthepassword', 'Enter the password')}
        />
      </Form.Item>
      {showReferralCodeInput ? (
        <Form.Item label={t('referralCode', 'Referral code')} name="referral_code">
          <Input autoComplete="off" placeholder={t('enterReferralCode', 'Enter referral code')} />
        </Form.Item>
      ) : null}
    </>
  );

  const signupSubmitButton = (
    <Button type="primary" htmlType="submit" block loading={auth.pending.signup}>
      {t('createAccount', 'Create Account')}
    </Button>
  );

  const signupLoginLink = (
    <div className="auth-link-container">
      <span className="auth-link-text">{t('alreadyHaveAccount', 'Already have an account?')}</span>
      <button className="auth-link-button" type="button" onClick={() => setActiveMode('login')}>
        {t('login', 'Log in')}
      </button>
    </div>
  );

  const modalWidth =
    activeMode === 'signup' && isBusiness
      ? '40rem'
      : activeMode === 'login' || activeMode === 'signup'
        ? '32rem'
        : '24rem';

  return (
    <AppModal
      kind="auth"
      open={open}
      footer={null}
      onCancel={close}
      width={modalWidth}
      centered
      destroyOnHidden
      zIndex={10000}
      className={`storefront-auth-modal${
        activeMode === 'signup' && isBusiness ? ' is-business-signup-modal' : ''
      }`}
    >
      {showChoice ? (
        <div className="storefront-auth-choice">
          <AuthBrandMark compact />
          <h2>{title === 'Almost there!' ? t('almostThere', 'Almost there!') : title}</h2>
          <p>
            {subtitle === 'To place your order, log in or sign up.'
              ? t('toPlaceYourOrder', 'To place your order, log in or sign up.')
              : subtitle}
          </p>
          <Button type="primary" block size="large" onClick={() => setActiveMode('login')}>
            {t('login', 'Login')}
          </Button>
          <Button block size="large" onClick={() => setActiveMode('signup')}>
            {t('signUp', 'Sign Up')}
          </Button>
        </div>
      ) : activeMode === 'login' ? (
        <div className="storefront-auth-form">
          <AuthBrandMark compact />
          <h2>{t('welcomeBack', 'Welcome back')}</h2>
          <p>{t('signInToTrackOrders', 'Log in to track orders and check out faster.')}</p>
          {auth.errors.login && !auth.errors.login?.fields ? (
            <Alert
              type="error"
              message={auth.errors.login?.message ?? t('signInFailed', 'Sign-in failed.')}
            />
          ) : null}
          <Form form={loginForm} layout="vertical" requiredMark={false} onFinish={onLogin}>
            <Form.Item label={t('loginWith', 'Login with')}>
              <Segmented
                block
                value={contactMethod}
                options={[
                  { label: t('email', 'Email'), value: 'email' },
                  { label: t('phone', 'Phone'), value: 'phone' },
                ]}
                onChange={(v) => setContactMethod(v)}
              />
            </Form.Item>
            {contactMethod === 'email' ? (
              <Form.Item label={t('email', 'Email')} name="email" rules={[{ required: true }]}>
                <Input
                  type="email"
                  autoComplete="email"
                  placeholder={t('enterEmailAddress', 'Enter email address')}
                />
              </Form.Item>
            ) : (
              phoneFields()
            )}
            <Form.Item
              label={t('password', 'Password')}
              name="password"
              rules={[{ required: true }]}
            >
              <Input.Password
                autoComplete="current-password"
                placeholder={t('enterPassword', 'Enter password')}
              />
            </Form.Item>
            <Button type="primary" htmlType="submit" block loading={auth.pending.login}>
              {t('login', 'Log in')}
            </Button>
          </Form>
          <button
            className="auth-link-button"
            type="button"
            onClick={() => setActiveMode('forgot')}
          >
            {t('forgotPasswordQuery', 'Forgot password?')}
          </button>
          <div className="auth-link-container">
            <span className="auth-link-text">{t('newHere', 'New here?')}</span>
            <button
              className="auth-link-button"
              type="button"
              onClick={() => setActiveMode('signup')}
            >
              {t('createAccount', 'Create account')}
            </button>
          </div>
        </div>
      ) : activeMode === 'signup' ? (
        <div className={`storefront-auth-form${isBusiness ? ' is-business-signup' : ''}`}>
          <AuthBrandMark compact />
          <h2>
            {isBusiness
              ? t('createBusinessAccount', 'Create business account')
              : t('createAccount', 'Create account')}
          </h2>
          <Form form={signupForm} layout="vertical" requiredMark={isBusiness} onFinish={onSignup}>
            {/* Business signup is long: its fields scroll while the submit button
                and login link stay pinned below them. */}
            {isBusiness ? (
              <div className="storefront-auth-scroll">{signupFields}</div>
            ) : (
              signupFields
            )}
            {isBusiness ? (
              <div className="storefront-auth-footer">
                {signupSubmitButton}
                {signupLoginLink}
              </div>
            ) : (
              signupSubmitButton
            )}
          </Form>
          {isBusiness ? null : signupLoginLink}
        </div>
      ) : activeMode === 'forgot' ? (
        <div className="storefront-auth-form">
          <AuthBrandMark compact />
          <h2>{t('forgotPassword', 'Forgot password')}</h2>
          <p>{t('sendOtpToReset', 'We will send an OTP to your email or phone.')}</p>
          <Form form={forgotForm} layout="vertical" requiredMark={false} onFinish={onForgot}>
            <Form.Item label={t('sendCodeVia', 'Send code via')}>
              <Segmented
                block
                value={contactMethod}
                options={[
                  { label: t('email', 'Email'), value: 'email' },
                  { label: t('phone', 'Phone'), value: 'phone' },
                ]}
                onChange={(v) => setContactMethod(v)}
              />
            </Form.Item>
            {contactMethod === 'email' ? (
              <Form.Item label={t('email', 'Email')} name="email" rules={[{ required: true }]}>
                <Input
                  type="email"
                  autoComplete="email"
                  placeholder={t('enterEmailAddress', 'Enter email address')}
                />
              </Form.Item>
            ) : (
              phoneFields()
            )}
            <Button type="primary" htmlType="submit" block loading={auth.pending.forgot}>
              {t('sendCode', 'Send code')}
            </Button>
          </Form>
          <div className="auth-link-container">
            <span className="auth-link-text">{t('backTo', 'Back to')}</span>
            <button
              className="auth-link-button"
              type="button"
              onClick={() => setActiveMode('login')}
            >
              {t('login', 'Log in')}
            </button>
          </div>
        </div>
      ) : activeMode === 'approval' ? (
        <div className="storefront-auth-form">
          <AuthBrandMark compact />
          <h2 style={{ textAlign: 'center' }}>{t('signupReceived', 'Signup received')}</h2>
          <p style={{ textAlign: 'center' }}>{approvalMessage}</p>
          <Button type="primary" block onClick={close}>
            {t('ok', 'OK')}
          </Button>
        </div>
      ) : activeMode === 'verify' ? (
        <div className="storefront-auth-form">
          <AuthBrandMark compact />
          <h2 style={{ textAlign: 'center' }}>{t('otpVerification', 'OTP Verification')}</h2>
          <p style={{ textAlign: 'center' }}>
            {t(
              'enterSixDigitCode',
              'Enter the 6 digit verification code recieved on your Email ID',
            )}
          </p>
          <Form form={verifyForm} layout="vertical" requiredMark={false} onFinish={onVerify}>
            <Form.Item label={t('verificationCode', 'Verification code')} name="otp" hidden>
              <Input />
            </Form.Item>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
                <span>{t('verificationCode', 'Verification code')}</span>
                <InfoCircleFilled style={{ color: '#8b95a7' }} />
                <span style={{ color: isOtpExpired ? '#cf1322' : '#1d39c4' }}>{timeLeftLabel}</span>
              </div>
              <button
                className="auth-link-button"
                type="button"
                onClick={() => onResend({})}
                disabled={!isOtpExpired || auth.pending.resend}
              >
                {t('resendOtp', 'Resend OTP')}
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 10 }}>
              {otpDigits.map((digit, idx) => (
                <Input
                  key={idx}
                  className="storefront-auth-otp-digit"
                  ref={(el) => {
                    otpInputRefs.current[idx] = el;
                  }}
                  value={digit}
                  maxLength={1}
                  inputMode="numeric"
                  placeholder="0"
                  onChange={(e) => onOtpDigitChange(idx, e.target.value)}
                  onKeyDown={(e) => onOtpKeyDown(idx, e)}
                  onPaste={onOtpPaste}
                  style={{ textAlign: 'center', height: 56, fontSize: 24, fontWeight: 600 }}
                />
              ))}
            </div>
            {isOtpExpired ? (
              <p style={{ color: '#cf1322', marginTop: 10 }}>
                {t('otpExpired', 'OTP expired. Please resend OTP.')}
              </p>
            ) : null}
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={auth.pending.verify}
              disabled={isOtpExpired || otpValue.length !== 6}
              style={{ marginTop: 14 }}
            >
              {t('verify', 'Verify')}
            </Button>
          </Form>
        </div>
      ) : activeMode === 'reset' ? (
        <div className="storefront-auth-form">
          <AuthBrandMark compact />
          <h2>{t('resetPassword', 'Reset password')}</h2>
          <p>{t('enterNewPassword', 'Enter your new password.')}</p>
          <Form form={resetForm} layout="vertical" requiredMark={false} onFinish={onReset}>
            <Form.Item
              label={t('newPassword', 'New password')}
              name="new_password"
              rules={[{ required: true }]}
            >
              <Input.Password
                autoComplete="new-password"
                placeholder={t('enterNewPassword', 'Enter new password')}
              />
            </Form.Item>
            <Form.Item
              label={t('confirmPassword', 'Confirm password')}
              name="confirm_password"
              rules={[{ required: true }]}
            >
              <Input.Password
                autoComplete="new-password"
                placeholder={t('confirmNewPassword', 'Confirm new password')}
              />
            </Form.Item>
            <Button type="primary" htmlType="submit" block loading={auth.pending.reset}>
              {t('resetPassword', 'Reset password')}
            </Button>
          </Form>
        </div>
      ) : (
        <div className="storefront-auth-form">
          <AuthBrandMark compact />
          <h2>{t('resendOtp', 'Resend OTP')}</h2>
          <p>{t('resendOtpDesc', 'Resend OTP using email or phone.')}</p>
          <Form form={resendForm} layout="vertical" requiredMark={false} onFinish={onResend}>
            <Form.Item label={t('sendCodeVia', 'Send code via')}>
              <Segmented
                block
                value={contactMethod}
                options={[
                  { label: t('email', 'Email'), value: 'email' },
                  { label: t('phone', 'Phone'), value: 'phone' },
                ]}
                onChange={(v) => setContactMethod(v)}
              />
            </Form.Item>
            {contactMethod === 'email' ? (
              <Form.Item label={t('email', 'Email')} name="email" rules={[{ required: true }]}>
                <Input
                  type="email"
                  autoComplete="email"
                  placeholder={t('enterEmailAddress', 'Enter email address')}
                />
              </Form.Item>
            ) : (
              phoneFields()
            )}
            <Button type="primary" htmlType="submit" block loading={auth.pending.resend}>
              {t('resendOtp', 'Resend OTP')}
            </Button>
          </Form>
        </div>
      )}
    </AppModal>
  );
}
